import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import LHModalPop from '../../../components/LHModalPop';
import { selectCentralizedErrors, selectIsErrorModalVisible, clearCentralizedErrors, setErrorModalVisible } from '../../../store/slices/nhoSlice';

const ACRONYMS = new Set(['NHO', 'API', 'GPID', 'DPS', 'CPH', 'SIA']);

const toTitleCase = (s: string) => {
  const lower = s.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

const getApiTitle = (apiName: string): string => {
  if (!apiName) return 'Error Failed';

  // If the API name uses underscores (SCREAMING_SNAKE_CASE or snake_case), split on underscores
  if (apiName.includes('_')) {
    const parts = apiName.split('_').filter(Boolean);
    const words = parts.map((part) => {
      // Preserve known acronyms (e.g., NHO, API)
      if (ACRONYMS.has(part.toUpperCase())) return part.toUpperCase();
      // Otherwise normalize to Title Case
      return toTitleCase(part);
    });
    return `${words.join(' ')} Failed`;
  }

  // Fallback: handle camelCase / PascalCase (e.g., 'fetchNHO' -> 'Fetch NHO')
  let words = apiName
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();

  // Compress sequences of single uppercase letters separated by spaces into an acronym
  // e.g. 'N H O' -> 'NHO'
  words = words.replace(/\b(?:[A-Z](?:\s+|$)){2,}\b/g, (match) => match.replace(/\s+/g, ''));

  return `${words} Failed`;
};

const NhoErrorModal: React.FC = () => {
  const dispatch = useDispatch();
  const errors = useSelector(selectCentralizedErrors);
  const isVisible = useSelector(selectIsErrorModalVisible);

  const activeError = errors.length > 0 ? errors[errors.length - 1] : null;

  const handleClose = () => {
    dispatch(setErrorModalVisible(false));
    dispatch(clearCentralizedErrors());
  };

  if (!activeError) return null;

  return (
    <LHModalPop
      visible={isVisible}
      onClose={handleClose}
      title={getApiTitle(activeError.apiName)}
      description={activeError.message}
      showFooter={true}
      primaryButtonText="OK"
      onPrimaryPress={handleClose}
      showCloseButton={true}
    />
  );
};

export default NhoErrorModal;
